import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { Clock, Loader2 } from "lucide-react";

import { MenuItem } from "@/model/entities";
import { cn } from "@/lib/utils";
import { useRestaurantDataLoader } from "@/hooks/useRestaurantDataLoader";
import { useMenuItemsByRestaurant } from "@/query/useMenuItems";
import { getVisitorId } from "@/utils/visitorUtils";
import { trackMenuView } from "@/service/analyticsService";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
// Menu item rendering moved to CategorySection2
import { ThemeProvider } from "@/components/ui/theme-provider";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

// Default assets
import defaultLogo from "@/assets/smrt-menu-logo.png";

// Development flag - set to true to use dummy data for testing
const USE_DUMMY_DATA_IN_DEV =
  import.meta.env.VITE_SHOW_DUMMY_DATA === "true" || false;

// Default data for new restaurants
const DEFAULT_RESTAURANT_NAME = "Your Restaurant";
const DEFAULT_WORKING_HOURS = "24 Hours";

// Default categories and menu items for demo
import { workingHoursService } from "@/service/workingHoursService";
import ImageCarousel from "@/components/ImageCarousel";
import {
  getDefaultCategories,
  getDummyDevelopmentCategories,
} from "@/utils/dummyDataUtils";
import CategorySection2 from "@/components/menuViewer/CategorySection2";
// Removed unused imports (icons, PhoneNumber, SocialLink) to avoid compile errors
import SocialLinkList from "@/components/menuViewer/SocialLinkList";

// Comprehensive dummy data for development testing

const MenuViewer2 = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    restaurantData: restaurant,
    categories,
    workingHours,
    activeCategory,
    loading,
    error,
  } = useRestaurantDataLoader();

  const [localActiveCategory, setLocalActiveCategory] = useState<string | null>(
    activeCategory
  );
  const categoryRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Use default values when restaurant data is empty
  const displayRestaurantName =
    restaurant?.restaurantName ||
    t("demo.restaurantName", DEFAULT_RESTAURANT_NAME);
  const displayLogo = restaurant?.restaurantLogo || defaultLogo;

  // Use default categories if no categories exist - memoized to avoid re-computation
  const displayCategories = useMemo(() => {
    if (USE_DUMMY_DATA_IN_DEV && import.meta.env.DEV) {
      return getDummyDevelopmentCategories(t);
    }
    if (categories && categories.length > 0) return categories;
    return getDefaultCategories(t);
  }, [categories, t]);

  // Fetch all menu items for the restaurant
  const { data: allMenuItems, isLoading: isMenuItemsLoading } =
    useMenuItemsByRestaurant(restaurant?.id ?? "");

  // Group menu items by category
  const itemsByCategory = React.useMemo(() => {
    // If using dummy data in development, extract items from category structure
    if (USE_DUMMY_DATA_IN_DEV && import.meta.env.DEV) {
      return displayCategories.reduce(
        (acc, category) => {
          if (category.id && category.items) {
            // keep only available items in dummy/dev mode as well
            acc[category.id] = (category.items || []).filter(
              (it) => it.isAvailable !== false
            );
          }
          return acc;
        },
        {} as Record<string, MenuItem[]>
      );
    }

    // Otherwise use real menu items from API
    if (!allMenuItems) return {};
    return allMenuItems.reduce(
      (acc, item) => {
        if (!item.categoryId) return acc;
        if (item.isAvailable === false) return acc; // skip unavailable items
        if (!acc[item.categoryId]) acc[item.categoryId] = [];
        acc[item.categoryId].push(item);
        return acc;
      },
      {} as Record<string, MenuItem[]>
    );
  }, [allMenuItems, displayCategories]);

  // Get images for carousel - use defaults if no menu items have images
  const menuImages = displayCategories
    .flatMap((category) =>
      category.id
        ? itemsByCategory[category.id]?.map((item) => item.imageUrl) || []
        : []
    )
    .filter((url): url is string => !!url);

  const dummyImages = getDummyDevelopmentCategories(t)
    .map((category) =>
      (category.items ?? []).flatMap((item) =>
        item.imageUrl ? [item.imageUrl] : []
      )
    )
    .flat();
  const displayImages = USE_DUMMY_DATA_IN_DEV ? dummyImages : menuImages;

  // Use default working hours if none exist
  const hasWorkingHours = workingHours && workingHours.length > 0;
  const displayWorkingHours = hasWorkingHours
    ? `${workingHours[0].open} - ${workingHours[0].close}`
    : DEFAULT_WORKING_HOURS;
  const displayIsOpen = hasWorkingHours
    ? workingHoursService.isOpen(workingHours, new Date())
    : true; // Default to open if no working hours

  // Track menu view when component mounts
  useEffect(() => {
    if (restaurant?.id && !USE_DUMMY_DATA_IN_DEV) {
      const visitorId = getVisitorId();
      // Fire and forget - don't block rendering
      trackMenuView(restaurant.id, visitorId, restaurant.id).catch((error) => {
        console.error("Failed to track menu view:", error);
      });
    }
  }, [restaurant?.id]);

  useEffect(() => {
    setLocalActiveCategory(activeCategory);
  }, [activeCategory]);

  const scrollToCategory = (categoryId: string) => {
    setLocalActiveCategory(categoryId);
    const button = categoryRefs.current[categoryId];
    const container = scrollContainerRef.current;
    if (button && container) {
      const scrollPos =
        button.offsetLeft - container.offsetWidth / 2 + button.offsetWidth / 2;
      container.scrollTo({ left: scrollPos, behavior: "smooth" });
    }
    const section = document.getElementById(`category-${categoryId}`);
    section?.scrollIntoView({ behavior: "smooth" });
  };

  const handleMenuItemClick = (categoryId: string, menuItem: MenuItem) => {
    // Check if we're in development mode with dummy data or if this is a demo item
    const isDummyMode = USE_DUMMY_DATA_IN_DEV && import.meta.env.DEV;
    const isDemoItem = menuItem.id?.startsWith("demo-");

    if (isDummyMode || isDemoItem) {
      // For dummy/demo items, navigate to a demo route or show a placeholder
      const demoUrl = `/menu/demo/category/${categoryId}/item/${menuItem.id}`;
      navigate(demoUrl);
    } else if (restaurant) {
      // For real restaurant items
      const restaurantUrl = `/menu/${restaurant.id}/category/${categoryId}/item/${menuItem.id}`;
      navigate(restaurantUrl);
    }
  };

  // Filter out categories that have no menu items so they are not displayed.
  // In dev dummy mode, categories may already include items so keep them as-is.
  const visibleCategories = useMemo(() => {
    if (USE_DUMMY_DATA_IN_DEV && import.meta.env.DEV) return displayCategories;
    return displayCategories.filter((cat) => {
      if (!cat.id || cat.isHidden) return false;
      const items = itemsByCategory[cat.id] || [];
      return items.length > 0;
    });
  }, [displayCategories, itemsByCategory]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-row items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-primary" />
        <span className="ml-4 text-xl font-bold rtl:mr-4 rtl:ml-0">
          {t("loading", "טוען תפריט...")}
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-12 text-center max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold text-red-500 mb-4 rtl:text-right">
          {t("error", "שגיאה")}
        </h1>
        <p className="rtl:text-right">{error}</p>
      </div>
    );
  }

  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem>
      <div
        className="min-h-screen w-full text-foreground"
        style={{ backgroundColor: "#e7e3f2" }}
      >
        {/* Image Carousel + Centered Logo */}
        <div className="relative w-full mb-6 border-b-4 border-[#7fb438] max-w-4xl mx-auto">
          <div className="relative w-full h-[250px] sm:h-[400px]">
            <ImageCarousel images={displayImages} />
            <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
              <Avatar className="h-36 w-36 border-4 border-[#7fb438] shadow-xl bg-white">
                <AvatarImage src={displayLogo} alt="Restaurant Logo" />
                <AvatarFallback className="text-2xl font-bold">
                  {displayRestaurantName.charAt(0)}
                </AvatarFallback>
              </Avatar>
            </div>
          </div>
        </div>

        {/* Header Section */}
        <div className="w-full pt-2 pb-6 max-w-4xl mx-auto">
          <div className="flex flex-col items-center gap-3 w-full">
            <h1
              className="text-2xl font-bold text-center text-primary rtl:text-right"
              data-testid="restaurant-name"
            >
              {displayRestaurantName}
            </h1>

            {/* Working Hours and Open/Closed Badge */}
            <div className="flex flex-row gap-2 items-center justify-center flex-wrap w-full">
              <Badge className="text-xs p-2 bg-[#7fb438] text-white flex items-center gap-1">
                <Clock className="h-3 w-3 text-white" />
                {displayWorkingHours}
              </Badge>

              <Badge
                className={cn(
                  "text-xs p-2 flex items-center gap-1",
                  displayIsOpen
                    ? "bg-[#7fb438] text-white"
                    : "text-white bg-red-800"
                )}
              >
                {displayIsOpen ? t("open", "פתוח") : t("closed", "סגור")}
              </Badge>
            </div>

            {/* social media links could go here */}
            <div className="flex flex-row gap-4 items-center justify-center">
              <SocialLinkList restaurant={restaurant!} />
            </div>
          </div>
        </div>

        {/* Category Navigation */}
        {visibleCategories.length > 0 && (
          <div className="w-full mb-6 max-w-4xl mx-auto px-3">
            <div
              className="flex flex-row gap-2 overflow-x-auto no-scrollbar pb-2 touch-auto"
              ref={scrollContainerRef}
            >
              {visibleCategories.map((category) => (
                <Button
                  key={category.id}
                  ref={(el) => {
                    categoryRefs.current[category.id || ""] = el;
                  }}
                  onClick={() => scrollToCategory(category.id || "")}
                  variant={
                    localActiveCategory === category.id
                      ? "default"
                      : "secondary"
                  }
                  size="sm"
                  className="rounded-full whitespace-nowrap flex-shrink-0 rtl:text-right"
                  data-testid="category-button"
                >
                  {category.name}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* Menu Items Grid */}
        <div className="w-full pb-20 max-w-4xl mx-auto px-3">
          <div className="flex flex-col gap-1">
            {visibleCategories.map((category) => {
              const menuItems = category.id
                ? itemsByCategory[category.id] || []
                : [];
              return (
                <CategorySection2
                  key={category.id}
                  category={category}
                  menuItems={menuItems}
                  isMenuItemsLoading={
                    isMenuItemsLoading &&
                    !(USE_DUMMY_DATA_IN_DEV && import.meta.env.DEV)
                  }
                  onMenuItemClick={handleMenuItemClick}
                />
              );
            })}
          </div>
        </div>
      </div>
    </ThemeProvider>
  );
};

export default MenuViewer2;
